import { Component, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatTableDataSource, MatDialog, MatSnackBar, PageEvent } from '@angular/material';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import { GatewayService } from 'src/app/gateway.service';
import { LocalSaveService } from 'src/app/services/local-save-service.service';
import { CrearGarantiaDialogComponent } from './crear-garantia-dialog/crear-garantia-dialog.component';
import { AtenderGarantiaDialogComponent } from './atender-garantia-dialog/atender-garantia-dialog.component';
import { VerGarantiaDialogComponent } from './ver-garantia-dialog/ver-garantia-dialog.component';
import { ConfirmacionComponent } from '../confirmacion/confirmacion.component';

/**
 * Tablero de Garantia Servicio D2D.
 *
 * Estados:
 *   0 = No atendida (gris, puede ser tomada)
 *   1 = Tomada     (amarillo, solo quien la tomo puede atender/anular)
 *   2 = Atendida   (verde, terminal)
 *   3 = Anulada    (rojo, terminal)
 *
 * Permisos via tienePermiso('GARANTIA_D2D', tipo):
 *   - ver:   ver el tablero
 *   - crear: crear nueva garantia
 *   - editar: tomar, atender y anular
 *
 * SLA visual: marca con clase 'sla-vencido' las garantias en estado 0 con
 * mas de 30 minutos desde fecha_creacion. Es puramente client-side.
 */
const SLA_MINUTOS = 30;

@Component({
  selector: 'app-garantia-d2d',
  templateUrl: './garantia-d2d.component.html',
  styleUrls: ['./garantia-d2d.component.scss']
})
export class GarantiaD2dComponent implements OnInit {

  displayedColumns: string[] = [
    'id', 'estado', 'nombre_cliente', 'cuenta', 'telefono',
    'servicio', 'problema',
    'falla',
    'fecha_creacion', 'creador', 'fecha_tomada', 'tomo',
    'sla', 'acciones'
  ];
  dataSource = new MatTableDataSource<any>([]);
  total = 0;
  page = 1;
  pageSize = 25;

  search = '';
  estadoFiltro: number | null = null;
  fechaDesde: string | null = null;
  fechaHasta: string | null = null;

  isLoading = false;
  isExporting = false;

  // Permisos resueltos al ngOnInit para evitar lookup en cada *ngIf
  puedeCrear = false;
  puedeEditar = false;
  puedeVer = false;
  myAgenteId: number | null = null;
  myNombre: string | null = null;
  myCorreo: string | null = null;

  // Opciones para el select de estado
  estadosOpciones = [
    { value: null, label: '— Todos —' },
    { value: 0, label: 'No atendida' },
    { value: 1, label: 'Tomada' },
    { value: 2, label: 'Atendida' },
    { value: 3, label: 'Anulada' },
  ];

  constructor(
    private gateway: GatewayService,
    private localSave: LocalSaveService,
    private snackbar: MatSnackBar,
    private dialog: MatDialog,
  ) {
    const user = this.localSave.get('usuarioPGV') || {};
    // Algunos roles (admin/supervisor) no tienen `idagente` sino `IDU` — patron
    // que se repite en otros endpoints del backend (user.IDU || user.idagente).
    this.myAgenteId = user.idagente != null ? Number(user.idagente)
                    : (user.IDU != null ? Number(user.IDU) : null);
    // Snapshot del usuario actual desde localStorage (no se lookup en BD).
    // El user de Admin/Distribuidor no esta en agente — vive en `distribuidor`
    // u otras tablas. Por eso resolvemos nombre+correo del lado cliente y los
    // pasamos al backend en cada crear/tomar/anular.
    const obj = user.OBJ || {};
    const nombreCombinado = ((obj.nombre || '') + ' ' + (obj.apellido || '')).trim();
    this.myNombre = nombreCombinado || (this.localSave.get('userLoggedIn') || null);
    this.myCorreo = obj.correo || null;
    this.puedeVer    = this._tienePermiso('GARANTIA_D2D', 'ver');
    this.puedeCrear  = this._tienePermiso('GARANTIA_D2D', 'crear');
    this.puedeEditar = this._tienePermiso('GARANTIA_D2D', 'editar');
  }

  ngOnInit() {
    if (this.puedeVer) {
      // Filtro por defecto: garantias creadas hoy. El usuario puede cambiarlo
      // con los inputs de fecha o limpiarlo con el boton de limpiar.
      const hoy = this._hoyYYYYMMDD();
      this.fechaDesde = hoy;
      this.fechaHasta = hoy;
      this.cargar();
    }
  }

  /** Devuelve la fecha de hoy local en formato YYYY-MM-DD (compatible con input type=date). */
  private _hoyYYYYMMDD(): string {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private _tienePermiso(modulo: string, tipo: 'ver' | 'crear' | 'editar' | 'borrar'): boolean {
    const user = this.localSave.get('usuarioPGV');
    if (!user || !user.permisos) { return false; }
    const perm = user.permisos.find((p: any) => p.modulo === modulo);
    if (!perm) { return false; }
    return perm['permiso_' + tipo] === 1;
  }

  cargar(): void {
    if (!this.puedeVer) { return; }
    this.isLoading = true;
    const payload = {
      page: this.page,
      pageSize: this.pageSize,
      estado: this.estadoFiltro,
      search: this.search ? this.search.trim() : null,
      fechaDesde: this.fechaDesde,
      fechaHasta: this.fechaHasta,
    };
    this.gateway.get('garantiaD2dListar', payload)
      .then((res: any) => {
        this.isLoading = false;
        this.dataSource.data = res && res.rows ? res.rows : [];
        this.total = res && res.total != null ? Number(res.total) : 0;
      })
      .catch(() => {
        this.isLoading = false;
        this.snackbar.open('Error al cargar garantías', 'Cerrar', { duration: 3000 });
      });
  }

  onPage(ev: PageEvent): void {
    this.page = ev.pageIndex + 1;
    this.pageSize = ev.pageSize;
    this.cargar();
  }

  onBuscar(): void {
    this.page = 1;
    this.cargar();
  }

  onLimpiar(): void {
    this.search = '';
    this.estadoFiltro = null;
    this.fechaDesde = null;
    this.fechaHasta = null;
    this.page = 1;
    this.cargar();
  }

  /**
   * Descarga un CSV con TODAS las garantías que cumplen los filtros actuales
   * (no solo la página visible). Pide al backend el listado completo con el
   * flag `all` y arma el archivo del lado cliente.
   */
  descargar(): void {
    if (!this.puedeVer || this.isExporting) { return; }
    this.isExporting = true;
    const payload = {
      all: true,
      estado: this.estadoFiltro,
      search: this.search ? this.search.trim() : null,
      fechaDesde: this.fechaDesde,
      fechaHasta: this.fechaHasta,
    };
    this.gateway.get('garantiaD2dListar', payload)
      .then((res: any) => {
        this.isExporting = false;
        const rows = res && res.rows ? res.rows : [];
        if (!rows.length) {
          this.snackbar.open('No hay garantías para descargar con esos filtros.', 'Cerrar', { duration: 3000 });
          return;
        }
        this._exportarXlsx(rows);
      })
      .catch(() => {
        this.isExporting = false;
        this.snackbar.open('Error al generar la descarga.', 'Cerrar', { duration: 3000 });
      });
  }

  /** Genera y dispara la descarga de un XLSX (SheetJS + FileSaver). */
  private _exportarXlsx(rows: any[]): void {
    const pipe = new DatePipe('en-US');
    const fmt = (v: any): string => v ? (pipe.transform(v, 'd/MM/y HH:mm', 'America/Panama') || '') : '';

    // Cada fila como objeto con las claves = encabezados (mismo orden que la tabla).
    const filas = rows.map(r => ({
      'ID': r.id,
      'Estado': r.estado_label,
      'Cliente': r.nombre_cliente || '',
      'Cuenta': r.cuenta || '',
      'Teléfono': r.telefono || '',
      'Servicio': r.tipo_servicio_nombre || '',
      'Problema': r.tipo_problema_nombre || '',
      'Falla': r.falla || '',
      'Comentario creación': r.comentario_creacion || '',
      'Creada por': r.nombre_creador || '',
      'Correo creador': r.correo_creador || '',
      'Creación': fmt(r.fecha_creacion),
      'Tomada por': r.nombre_tomo || '',
      'Correo tomó': r.correo_tomo || '',
      'Tomada': fmt(r.fecha_tomada),
      'Comentario atención': r.comentario_atencion || '',
      'Atendida': fmt(r.fecha_atendida),
      'Comentario anulación': r.comentario_anulacion || '',
      'Anulada por': r.nombre_anulo || '',
      'Anulada': fmt(r.fecha_anulada),
    }));

    const headers = Object.keys(filas[0]);
    const worksheet = XLSX.utils.json_to_sheet(filas, { header: headers });
    const workbook = { Sheets: { 'Garantías': worksheet }, SheetNames: ['Garantías'] };
    const wbout: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    FileSaver.saveAs(blob, 'garantias_servicio_' + this._hoyYYYYMMDD() + '.xlsx');
  }


  // ─── Acciones ─────────────────────────────────────────────────────────────

  crearGarantia(): void {
    const ref = this.dialog.open(CrearGarantiaDialogComponent, {
      width: '500px',
      disableClose: true,
    });
    ref.afterClosed().subscribe(r => {
      if (r && r.creada) {
        this.snackbar.open('Garantía registrada correctamente.', 'Cerrar', { duration: 2500 });
        this.cargar();
      }
    });
  }

  tomarGarantia(row: any): void {
    if (!this.myAgenteId) { return; }
    this.gateway.get('garantiaD2dTomar', {
      id: row.id,
      id_agente_tomo: this.myAgenteId,
      nombre_tomo:    this.myNombre,
      correo_tomo:    this.myCorreo,
    })
      .then((res: any) => {
        if (Number(res.response) === 1) {
          this.snackbar.open(res.message || 'Garantía tomada.', 'Cerrar', { duration: 2500 });
          this.cargar();
          // Abre directo el dialog de atender con la garantia recien tomada
          if (res.garantia) {
            this._abrirAtender(res.garantia);
          }
        } else {
          this.snackbar.open(res.message || 'No se pudo tomar.', 'Cerrar', { duration: 4000 });
          this.cargar();
        }
      })
      .catch(() => {
        this.snackbar.open('Error al tomar la garantía.', 'Cerrar', { duration: 3000 });
      });
  }

  atenderGarantia(row: any): void {
    this._abrirAtender(row);
  }

  /** Abre el dialog read-only con todos los datos + ambos comentarios. */
  verDetalle(row: any): void {
    this.dialog.open(VerGarantiaDialogComponent, {
      width: '600px',
      data: { garantia: row },
    });
  }

  private _abrirAtender(row: any): void {
    const ref = this.dialog.open(AtenderGarantiaDialogComponent, {
      width: '550px',
      disableClose: true,
      data: { garantia: row },
    });
    ref.afterClosed().subscribe(r => {
      if (r && (r.atendida || r.anulada)) {
        this.cargar();
      }
    });
  }


  // ─── Helpers de UI ────────────────────────────────────────────────────────

  /** Devuelve true si la garantia tiene SLA vencido (>30 min en estado 0). */
  slaVencido(row: any): boolean {
    if (!row || Number(row.estado) !== 0) { return false; }
    return Number(row.minutos_desde_creacion || 0) > SLA_MINUTOS;
  }

  /** Devuelve true si el agente actual es quien tomo la garantia. */
  laTomeYo(row: any): boolean {
    return this.myAgenteId != null && Number(row.id_agente_tomo) === this.myAgenteId;
  }

  /** Clase CSS por estado para el chip. */
  estadoClass(estado: number): string {
    switch (Number(estado)) {
      case 0: return 'estado-pendiente';
      case 1: return 'estado-tomada';
      case 2: return 'estado-atendida';
      case 3: return 'estado-anulada';
      default: return '';
    }
  }
}
